const state = {
  multi: true,
  allowImage: false,
  allowDetails: false,
  banners: [],
  loading: false,
  message: '',
  alertVariant: 'success',
  showDismissibleAlert: false,
  uploadProgress: 0
}

const mutations = {
  setBanners: function (state, banners) {
    state.banners = banners
  },
  setLoading: function (state, loading) {
    state.loading = loading
  },
  setUploadProgress: function (state, progress) {
    state.uploadProgress = progress
  },
  showAlert: function (state, payload) {
    state.alertVariant = payload.variant
    state.message = payload.message
    state.showDismissibleAlert = true
  },
  closeDismissibleAlert: function (state, dismiss) {
    state.showDismissibleAlert = dismiss
  }
}

function cleanFileName (fileName) {
  return fileName.replace(/[^a-zA-Z0-9._-]/g, '_')
}

function toDateString (value) {
  if (!value) {
    return ''
  }
  if (value.toDate) {
    return value.toDate().toLocaleString()
  }
  return value
}

function bannerCollection (context) {
  return context.rootState.firestoreDB.collection('banners')
}

function currentUserEmail (context) {
  return context.rootState.currentUser ? context.rootState.currentUser.email : ''
}

const actions = {
  fetchBanners (context) {
    context.commit('setLoading', true)
    bannerCollection(context).orderBy('sortOrder', 'asc').get()
      .then(function (querySnapshot) {
        let banners = []
        querySnapshot.forEach(function (doc) {
          let data = doc.data()
          banners.push({
            docId: doc.id,
            title: data.title || '',
            description: data.description || '',
            article: data.article || '',
            imageUrl: data.imageUrl || '',
            storagePath: data.storagePath || '',
            active: data.active === true,
            sortOrder: data.sortOrder || 0,
            createdDateDisplay: toDateString(data.createdDate),
            updatedDateDisplay: toDateString(data.updatedDate)
          })
        })
        context.commit('setBanners', context.state.multi ? banners : banners.slice(0, 1))
        context.commit('setLoading', false)
      }).catch(function (err) {
        console.error('Error while fetching banners: ' + err)
        context.commit('setLoading', false)
        context.commit('showAlert', { variant: 'danger', message: 'Error loading banners: ' + err })
      })
  },
  saveSingleBanner (context, payload) {
    let title = payload && payload.title ? payload.title.trim() : ''
    if (!title) {
      context.commit('showAlert', { variant: 'warning', message: 'Please enter a banner title.' })
      return
    }

    context.commit('setLoading', true)
    bannerCollection(context).orderBy('sortOrder', 'asc').limit(1).get()
      .then(function (querySnapshot) {
        if (querySnapshot.empty) {
          return bannerCollection(context).add({
            title: title,
            description: '',
            article: '',
            imageUrl: '',
            storagePath: '',
            active: true,
            sortOrder: 0,
            createdBy: currentUserEmail(context),
            createdDate: context.rootState.firebase.firestore.FieldValue.serverTimestamp(),
            updatedBy: '',
            updatedDate: null
          })
        }

        let doc = querySnapshot.docs[0]
        let data = doc.data() || {}
        return bannerCollection(context).doc(doc.id).update({
          title: title,
          active: data.active === true,
          updatedBy: currentUserEmail(context),
          updatedDate: context.rootState.firebase.firestore.FieldValue.serverTimestamp()
        })
      }).then(function () {
        context.commit('setLoading', false)
        context.commit('showAlert', { variant: 'success', message: 'Banner announcement saved.' })
        context.dispatch('fetchBanners')
      }).catch(function (err) {
        console.error('Error while saving banner announcement: ' + err)
        context.commit('setLoading', false)
        context.commit('showAlert', { variant: 'danger', message: 'Save failed: ' + err.message })
      })
  },
  uploadBanner (context, payload) {
    if (!context.state.multi) {
      context.dispatch('saveSingleBanner', payload)
      return
    }

    context.commit('setLoading', true)
    context.commit('setUploadProgress', 0)

    let saveBanner = function (downloadURL, storagePath) {
      return bannerCollection(context).add({
        title: payload.title || '',
        description: context.state.allowDetails ? (payload.description || '') : '',
        article: context.state.allowDetails ? (payload.article || '') : '',
        imageUrl: downloadURL || '',
        storagePath: storagePath || '',
        active: payload.active === true,
        sortOrder: Number(payload.sortOrder) || 0,
        createdBy: currentUserEmail(context),
        createdDate: context.rootState.firebase.firestore.FieldValue.serverTimestamp(),
        updatedBy: '',
        updatedDate: null
      })
    }

    if (!context.state.allowImage) {
      saveBanner('', '').then(function () {
        context.commit('setLoading', false)
        context.commit('setUploadProgress', 0)
        context.commit('showAlert', { variant: 'success', message: 'Banner uploaded.' })
        context.dispatch('fetchBanners')
      }).catch(function (err) {
        console.error('Error while saving banner: ' + err)
        context.commit('setLoading', false)
        context.commit('showAlert', { variant: 'danger', message: 'Banner save failed: ' + err.message })
      })
      return
    }

    if (!payload || !payload.file) {
      context.commit('setLoading', false)
      context.commit('showAlert', { variant: 'warning', message: 'Please choose an image first.' })
      return
    }

    let fileName = Date.now() + '_' + cleanFileName(payload.file.name)
    let storagePath = 'banners/' + fileName
    let storageRef = context.rootState.fbStorage.ref().child(storagePath)
    let uploadTask = storageRef.put(payload.file)

    uploadTask.on('state_changed', function (snapshot) {
      let progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
      context.commit('setUploadProgress', Math.round(progress))
    }, function (error) {
      console.error('Error while uploading banner: ' + error)
      context.commit('setLoading', false)
      context.commit('showAlert', { variant: 'danger', message: 'Upload failed: ' + error.message })
    }, function () {
      uploadTask.snapshot.ref.getDownloadURL().then(function (downloadURL) {
        return saveBanner(downloadURL, storagePath)
      }).then(function () {
        context.commit('setLoading', false)
        context.commit('setUploadProgress', 0)
        context.commit('showAlert', { variant: 'success', message: 'Banner uploaded.' })
        context.dispatch('fetchBanners')
      }).catch(function (err) {
        console.error('Error while saving banner: ' + err)
        context.commit('setLoading', false)
        context.commit('showAlert', { variant: 'danger', message: 'Banner save failed: ' + err.message })
      })
    })
  },
  updateBanner (context, banner) {
    if (!banner || !banner.docId) {
      return
    }
    context.commit('setLoading', true)

    let updateData = context.state.multi
      ? {
        title: banner.title || '',
        description: context.state.allowDetails ? (banner.description || '') : '',
        article: context.state.allowDetails ? (banner.article || '') : '',
        active: banner.active === true,
        sortOrder: Number(banner.sortOrder) || 0,
        updatedBy: currentUserEmail(context),
        updatedDate: context.rootState.firebase.firestore.FieldValue.serverTimestamp()
      }
      : {
        title: banner.title || '',
        active: banner.active === true,
        updatedBy: currentUserEmail(context),
        updatedDate: context.rootState.firebase.firestore.FieldValue.serverTimestamp()
      }

    bannerCollection(context).doc(banner.docId).update(updateData).then(function () {
      context.commit('setLoading', false)
      context.commit('showAlert', { variant: 'success', message: 'Banner updated.' })
      context.dispatch('fetchBanners')
    }).catch(function (err) {
      console.error('Error while updating banner: ' + err)
      context.commit('setLoading', false)
      context.commit('showAlert', { variant: 'danger', message: 'Update failed: ' + err.message })
    })
  },
  deleteBanner (context, banner) {
    if (!context.state.multi) {
      context.commit('showAlert', { variant: 'warning', message: 'Single banner mode does not allow delete.' })
      return
    }

    if (!banner || !banner.docId) {
      return
    }
    context.commit('setLoading', true)
    let deleteImagePromise = Promise.resolve()
    if (banner.storagePath) {
      deleteImagePromise = context.rootState.fbStorage.ref().child(banner.storagePath).delete().catch(function (err) {
        console.warn('Could not delete banner image from storage: ' + err)
      })
    }
    deleteImagePromise.then(function () {
      return bannerCollection(context).doc(banner.docId).delete()
    }).then(function () {
      context.commit('setLoading', false)
      context.commit('showAlert', { variant: 'success', message: 'Banner deleted.' })
      context.dispatch('fetchBanners')
    }).catch(function (err) {
      console.error('Error while deleting banner: ' + err)
      context.commit('setLoading', false)
      context.commit('showAlert', { variant: 'danger', message: 'Delete failed: ' + err.message })
    })
  }
}

const getters = {
  bannerFields: function (state) {
    if (!state.multi) {
      return [
        { key: 'title', label: 'Title' },
        { key: 'active', label: 'Visible' },
        { key: 'actions', label: 'Actions' }
      ]
    }

    let fields = [
      { key: 'title', label: 'Title' },
      { key: 'sortOrder', label: 'Sort' },
      { key: 'active', label: 'Active' },
      { key: 'createdDateDisplay', label: 'Created' },
      { key: 'actions', label: 'Actions' }
    ]

    if (state.allowDetails) {
      fields.splice(1, 0, { key: 'description', label: 'Description' }, { key: 'article', label: 'Article' })
    }

    if (state.allowImage) {
      fields.unshift({ key: 'preview', label: 'Preview' })
    }

    return fields
  }
}

export default { namespaced: true, state, mutations, actions, getters }



// WEBPACK FOOTER //
// ./src/store/bannerstoremodule.js